import { ArrowLeft, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { studentApi } from '../lib/api';
import type { AttachmentPolicy, TicketDetail, TicketSummary, TicketType } from '../lib/types';
import { NewTicketForm, ReplyBox, StatusTag, TicketRow, TicketThread, typeLabel } from '../ui/Tickets';

/**
 * Student Portal → Help & Feedback (#414): a student asks ClassGuardian support a question, reports a problem or shares
 * an idea. Their parents see these tickets too. (The Help button in the header is different: it asks a parent.)
 */
export function StudentSupport() {
  const [tickets, setTickets] = useState<TicketSummary[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState('');
  const [allowed, setAllowed] = useState<TicketType[] | undefined>(undefined);
  const [attachments, setAttachments] = useState<AttachmentPolicy | null>(null);
  useEffect(() => {
    studentApi
      .get<{ types: TicketType[]; attachments?: AttachmentPolicy }>('/api/student/support/options')
      .then((o) => (setAllowed(o.types), setAttachments(o.attachments ?? null)))
      .catch(() => undefined);
  }, []);

  const load = useCallback(() => {
    studentApi.get<TicketSummary[]>('/api/student/support/tickets').then(setTickets).catch(() => setTickets([]));
  }, []);
  useEffect(load, [load]);

  if (openId) return <StudentTicket id={openId} attachments={attachments} onBack={() => (setOpenId(null), load())} />;

  return (
    <div className="stack">
      <section className="card stack">
        <h2 className="student-section-title">Help &amp; Feedback</h2>
        <p className="muted small">
          Ask ClassGuardian a question, tell us something isn’t working, or share an idea. Your parents can see what you send.
        </p>
        {writing ? (
          <>
            <NewTicketForm
              allowAccount={false}
              allowed={allowed}
              attachments={attachments}
              onSubmit={async (t) => {
                setError('');
                try {
                  const made = await studentApi.post<TicketDetail>('/api/student/support/tickets', t);
                  setWriting(false);
                  setOpenId(made.id);
                } catch (e) {
                  setError((e as Error).message);
                }
              }}
            />
            {error && <p className="bad-text small">{error}</p>}
            <div>
              <button className="btn" onClick={() => setWriting(false)}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div>
            <button className="btn primary" onClick={() => setWriting(true)}>
              <Plus size={16} /> New request
            </button>
          </div>
        )}
      </section>
      <section className="card stack">
        <h2 className="student-section-title">Your requests</h2>
        {!tickets ? (
          <p className="muted">Loading…</p>
        ) : !tickets.length ? (
          <p className="muted">Nothing yet.</p>
        ) : (
          tickets.map((t) => <TicketRow key={t.id} t={t} onOpen={() => setOpenId(t.id)} />)
        )}
      </section>
    </div>
  );
}

function StudentTicket({ id, onBack, attachments }: { id: string; onBack: () => void; attachments: AttachmentPolicy | null }) {
  const [t, setT] = useState<TicketDetail | null | undefined>(undefined);
  const [error, setError] = useState('');
  useEffect(() => {
    studentApi.get<TicketDetail>(`/api/student/support/tickets/${id}`).then(setT).catch(() => setT(null));
  }, [id]);
  return (
    <div className="stack">
      <div>
        <button className="btn small" onClick={onBack}>
          <ArrowLeft size={15} /> All requests
        </button>
      </div>
      {t === undefined ? (
        <p className="muted">Loading…</p>
      ) : t === null ? (
        <p className="muted">That request isn’t here anymore.</p>
      ) : (
        <section className="card stack">
          <div className="row-between">
            <span className="muted small">
              #{t.number} · {typeLabel(t.type)}
            </span>
            <StatusTag status={t.status} />
          </div>
          <h2 className="student-section-title">{t.subject}</h2>
          <TicketThread messages={t.messages} issue={{ number: t.issueNumber }} files="/api/student/support/attachments" who="student" />
          {t.status === 'closed' ? (
            <p className="muted small">This request is closed. Start a new one if you still need help.</p>
          ) : (
            <ReplyBox
              attachments={attachments}
              onSend={async (text, files) => {
                setError('');
                try {
                  setT(await studentApi.post<TicketDetail>(`/api/student/support/tickets/${t.id}/messages`, { body: text, attachments: files }));
                  return true;
                } catch (e) {
                  setError((e as Error).message);
                  return false;
                }
              }}
            />
          )}
          {error && <p className="bad-text small">{error}</p>}
        </section>
      )}
    </div>
  );
}

/** How many of the student's requests have a reply they haven't opened (#414 phase 2). */
export function useStudentSupportUnread(tab: string) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let live = true;
    const check = () =>
      studentApi
        .get<{ count: number }>('/api/student/support/unread')
        .then((r) => live && setCount(r.count))
        .catch(() => undefined);
    void check();
    const timer = setInterval(check, 3 * 60_000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [tab]);
  return count;
}
